<script setup lang="ts">
  import { message } from "ant-design-vue";
  import type { UploadFile, UploadProps } from "ant-design-vue";
  import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useApi } from "~/composables/useApi";
  import Common from "~/utils/common";
  import { PlusOutlined } from "@ant-design/icons-vue";

  interface FeedbackForm {
    type: string | null;
    desc: string;
  }

  // 路由和API
  const route = useRoute();
  const router = useRouter();
  const api = useApi();

  // 状态管理
  const success = ref(false);
  const acceptType = ".jpg,.jpeg,.png";
  const typeList = ref<string[]>([]);
  const form = reactive<FeedbackForm>({
    type: null,
    desc: "",
  });
  const sourceUrl = ref(process.client ? location.href : "");
  const fileList = ref<UploadFile[]>([]);

  // 组件状态
  const state = reactive({
    textareaFocus: false,
    previewVisible: false,
    previewImage: "",
    maxImageCount: 3,
    submit: false,
    submitLoading: false,
    uploadLoading: false,
  });

  // 常量
  const maxFileSize = 10;
  const suffixList = ["jpg", "jpeg", "png"];

  // 计算属性
  const validate1 = computed(() => !form.type && state.submit);
  const validate2 = computed(() => !form.desc.trim() && state.submit);
  const isUpdate = computed(() => {
    if (success.value) return false;
    const initForm = JSON.stringify({
      type: null,
      desc: "",
    });
    const formData = JSON.stringify(form);
    return initForm !== formData || fileList.value.length > 0;
  });

  // 生命周期钩子
  onMounted(() => {
    const { url } = route.query;
    if (url && typeof url === "string") {
      sourceUrl.value = url;
    }
    loadTypes();
    window.addEventListener("beforeunload", onBeforeunload);
  });

  onUnmounted(() => {
    window.removeEventListener("beforeunload", onBeforeunload);
  });

  // 方法
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const onBeforeunload = (e: BeforeUnloadEvent) => {
    if (isUpdate.value) {
      e.preventDefault();
      e.returnValue = "系统可能不会保存您所做的更改？";
      return e.returnValue;
    }
  };

  const loadTypes = async () => {
    try {
      const res = await api.getFeedbackTypes();
      typeList.value = res;
    } catch (error) {
      console.error(error);
      message.error("获取反馈类型失败");
    }
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isLtM = file.size / 1024 / 1024 < maxFileSize;
    if (!isLtM) {
      message.warning(`文件大小不能超过${maxFileSize}M`);
      return false;
    }

    const suffix = Common.getFileSuffix(file.name);
    if (!suffixList.includes(suffix)) {
      message.warning(`只能上传${suffixList.join("、")}文件`);
      return false;
    }

    if (state.uploadLoading) {
      message.warning("请等待文件上传完成后再操作");
      return false;
    }

    fileUpload(file);
    return false;
  };

  const fileUpload = async (file: File) => {
    const blob = new Blob([file]);
    const urlObject = window.URL || window.webkitURL || window;
    const fileUrl = urlObject.createObjectURL(blob);

    const fileData: UploadFile = {
      uid: Common.getUUID(),
      name: file.name,
      status: "uploading",
      url: fileUrl,
    };

    fileList.value = [...fileList.value, fileData];
    message.loading({ content: "文件上传中", key: "fileUpload", duration: 0 });
    state.uploadLoading = true;

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.fileUpload(formData);
      fileData.status = "done";
      fileData.url = res;
      message.success({ content: "上传成功", key: "fileUpload" });
    } catch {
      fileData.status = "error";
      message.error({ content: "上传失败", key: "fileUpload" });
    } finally {
      state.uploadLoading = false;
    }
  };

  const onSubmit = async () => {
    state.submit = true;
    const { type, desc } = form;

    if (validate1.value || validate2.value) {
      message.warning("请检查必填项是否填写完整");
      return;
    }

    if (state.uploadLoading) {
      message.warning("请等待文件上传完成后再操作");
      return;
    }

    state.submitLoading = true;
    try {
      const images = fileList.value.filter((item) => item.status === "done").map((item) => item.url);

      const params = {
        feedbackType: type,
        feedbackDesc: desc.trim(),
        pictureUrls: images,
        feedbackUrl: sourceUrl.value,
      };

      await api.feedbackAdd(params);
      success.value = true;
      window.scrollTo(0, 0);
    } finally {
      state.submitLoading = false;
    }
  };

  const onFileChange = (e: any) => {
    const { fileList: list } = e;
    state.previewVisible = false;
    const uidList = list.map((item: any) => item.uid);
    fileList.value = fileList.value.filter((item) => uidList.includes(item.uid));
  };

  const onPreview: UploadProps["onPreview"] = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    state.previewImage = file.url || file.preview || "";
    state.previewVisible = true;
  };

  const toHelp = () => {
    router.push({ path: "/help/" });
  };
</script>

<template>
  <div class="container">
    <div v-if="success" class="success-wrap">
      <div class="icon">
        <icon-font type="xuanzhong" />
        <!-- <Icon name="custom:xuanzhong" />-->
      </div>
      <div class="title">提交成功，感谢您的反馈</div>
      <div class="sub-title">
        <div>我们会认真查看您的反馈和建议，并尽快处理</div>
        <div>您还可以在得理帮助中心查看更多使用技巧</div>
      </div>
      <div class="btns">
        <a-button type="primary" ghost @click="toHelp">访问帮助中心</a-button>
      </div>
    </div>
    <div v-else class="form">
      <div class="form-item">
        <div class="form-label">
          <label>* 反馈类型</label>
          <span v-if="validate1" class="form-label-error">请选择</span>
        </div>
        <div class="form-wrapper">
          <div class="radio-group">
            <div
              v-for="(item, i) in typeList"
              :key="i"
              class="radion-button"
              :class="{ active: form.type === item }"
              @click="form.type = item">
              {{ item }}
            </div>
          </div>
        </div>
      </div>
      <div class="form-item">
        <div class="form-label">
          <label>* 具体描述</label>
          <span v-if="validate2" class="form-label-error">请输入</span>
        </div>
        <div class="form-wrapper">
          <div class="textarea" tabindex="-1" :class="{ focus: state.textareaFocus }">
            <a-textarea
              v-model:value="form.desc"
              class="content roll"
              :maxlength="200"
              placeholder="请具体描述您的建议或问题，您的反馈会帮我们做的更好"
              @focus="state.textareaFocus = true"
              @blur="state.textareaFocus = false" />
            <span class="textarea-tip">{{ form.desc.length }}/200</span>
          </div>
        </div>
      </div>

      <div class="form-item">
        <div class="form-label">
          <label>添加图片</label>
        </div>
        <div class="form-wrapper">
          <a-upload
            :file-list="fileList"
            class="upload-file"
            list-type="picture-card"
            :accept="acceptType"
            :before-upload="beforeUpload"
            @preview="onPreview"
            @change="onFileChange">
            <div v-if="fileList.length < state.maxImageCount" class="file-add">
              <PlusOutlined />
              <div class="upload-text">
                <span>{{ fileList.length }}</span>
                <span>/</span>
                <span>{{ state.maxImageCount }}</span>
              </div>
            </div>
          </a-upload>
          <div class="file-tip">支持 10M 内的JPG、JPEG、PNG格式的图片</div>
          <a-modal v-model:open="state.previewVisible" :footer="null" width="62.5rem">
            <img style="width: 100%" :src="state.previewImage" />
          </a-modal>
        </div>
      </div>

      <div class="footer">
        <a-button type="primary" :loading="state.submitLoading" @click="onSubmit">提交</a-button>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  :deep(.ant-modal) {
    .ant-modal-close {
      top: 10px;
      right: 10px;
    }
  }

  .container {
    padding-top: 42px;
  }

  .success-wrap {
    padding-top: 88px;
    text-align: center;

    .icon {
      margin-bottom: 24px;

      .anticon {
        font-size: 72px;
        color: #02b42a;
      }
    }

    .title {
      margin-bottom: 24px;
      font-size: 20px;
      font-weight: 600;
      color: #1d2129;
    }

    .sub-title {
      font-size: 16px;
      line-height: 32px;
      color: #4e5969;
    }

    .btns {
      padding-top: 42px;

      button {
        height: 56px;
        padding: 0 38px;
        font-size: 18px;
      }
    }
  }

  .form {
    .form-item {
      .form-label {
        display: flex;
        align-items: center;

        label {
          font-size: 20px;
          font-weight: 600;
          line-height: 28px;
          color: #1d2129;
        }

        .form-label-error {
          padding-left: 8px;
          font-size: 16px;
          color: #f53f40;
        }
      }

      .form-wrapper {
        padding-top: 24px;
        padding-bottom: 42px;
      }
    }
  }

  .radio-group {
    display: flex;

    .radion-button {
      height: 38px;
      padding: 0 12px;
      margin-right: 24px;
      font-size: 14px;
      line-height: 38px;
      color: #1f2329;
      cursor: pointer;
      border: #dce5ec solid 1px;
      border-radius: 4px;
      transition: ease 300ms all;

      &:hover,
      &.active {
        color: #2f78ff;
        background: #f5f9ff;
        border-color: #2f78ff;
      }
    }
  }

  .textarea {
    position: relative;
    padding: 16px;
    padding-right: 8px;
    padding-bottom: 52px;
    margin-bottom: 18px;
    background: #fff;
    border: 1px solid #dce5ec;
    border-radius: 4px;
    transition: ease 300ms all;

    &.focus {
      border-color: #2f78ff;
    }

    .textarea-tip {
      position: absolute;
      right: 24px;
      bottom: 16px;
      font-size: 14px;
      color: #8f9bb3;
    }

    .content {
      height: 74px;
      padding: 0;
      font-size: 15px;
      color: #1d2129;
      resize: none;
      border: 0;
      box-shadow: none;
    }
  }

  .upload-file {
    :deep(.ant-upload-select-picture-card) {
      width: 120px;
      height: 120px;
      margin-bottom: 4px;
      background: #fcfcfc;
      border: #dce5ec solid 1px;
      border-radius: 4px;

      .file-add {
        .anticon {
          font-size: 26px;
          color: #8f9bb3;
        }

        .upload-text {
          font-size: 14px;
          color: #8f9bb3;
        }
      }
    }

    :deep(.ant-upload-list-picture-card-container) {
      width: 120px;
      height: 120px;
      margin-bottom: 4px;

      .ant-upload-list-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: 0;
        border-radius: 4px;

        .ant-upload-list-item-uploading-text {
          margin: 0;
        }
      }
    }
  }

  .file-tip {
    font-size: 14px;
    color: #8f9bb3;
  }

  .footer {
    button {
      min-width: 140px;
      height: 56px;
      font-size: 20px;
      letter-spacing: -1px;
    }
  }
</style>
